import { useState, FormEvent } from 'react';

const SERVICES_LIST = [
  'HVAC', 'Plumbing', 'Electrical', 'Porches & Decks',
  'Roofing & Siding', 'Painting', 'Remodeling & Renovations',
  'Construction Management', 'Commercial Buildouts', 'Other',
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [message, setMessage] = useState('');
  const [msgLen, setMsgLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (msgLen > 500) return;
    setStatus('submitting');

    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);

    try {
      const res = await fetch('https://readdy.ai/api/form/d75abvn5hic0eqh31igg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setMsgLen(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-0">
      <div className="flex flex-col lg:flex-row min-h-[700px]">
        {/* Left Panel */}
        <div className="w-full lg:w-5/12 bg-red-700 px-8 md:px-14 py-16 flex flex-col justify-center">
          <div className="text-red-200 text-xs font-semibold tracking-widest uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            Get In Touch
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            READY TO START
            <br />
            <span className="text-blue-300">YOUR LEGACY?</span>
          </h2>
          <p className="text-red-100 text-base leading-relaxed mb-10" style={{ fontFamily: "'Inter', sans-serif" }}>
            Fill out the form and we&apos;ll get back to you within 24 hours with a free, no-obligation quote.
          </p>

          <div className="flex flex-col gap-5 mb-10">
            {[
              { icon: 'ri-shield-check-line', text: 'Licensed & Insured in Illinois' },
              { icon: 'ri-calendar-check-line', text: '11+ Years of Trusted Service' },
              { icon: 'ri-money-dollar-circle-line', text: 'Free Consultations Available' },
              { icon: 'ri-map-pin-2-line', text: 'Serving All of Chicagoland' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                  <i className={`${item.icon} text-white text-base`} />
                </div>
                <span className="text-white/90 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          {/* Contact Info */}
          <div className="border-t border-white/20 pt-8 flex flex-col gap-3">
            <a href="tel:+13123990278" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-base" />
              </div>
              <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>312-399-0278</span>
            </a>
            <a href="mailto:build@bfclegacy.com" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-mail-line text-base" />
              </div>
              <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>build@bfclegacy.com</span>
            </a>
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-line text-base" />
              </div>
              <span className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>Chicago, IL &amp; Surrounding Suburbs</span>
            </div>
          </div>
        </div>

        {/* Right Panel — Form */}
        <div className="w-full lg:w-7/12 bg-white px-8 md:px-14 py-16 flex items-center">
          <div className="w-full max-w-lg">
            {status === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-green-600 text-3xl" />
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  MESSAGE SENT!
                </h3>
                <p className="text-gray-500 text-base" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Thanks for reaching out! We&apos;ll get back to you within 24 hours with your free quote.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 bg-red-700 hover:bg-red-800 text-white font-semibold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form id="d75abvn5hic0eqh31igg" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-700 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="John Smith"
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-500 transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-700 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-500 transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-700 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(312) 555-0100"
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-500 transition-colors"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-gray-700 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Service Needed
                    </label>
                    <select
                      name="service_needed"
                      className="border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-red-500 transition-colors cursor-pointer bg-white"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      <option value="">Select a service</option>
                      {SERVICES_LIST.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-700 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Project Address / Area
                  </label>
                  <input
                    type="text"
                    name="project_address"
                    placeholder="Chicago, IL or zip code"
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-500 transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-700 tracking-wide uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Project Details *
                    </label>
                    <span className={`text-xs ${msgLen > 500 ? 'text-red-500' : 'text-gray-400'}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                      {msgLen}/500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your project — what needs to be done, timeline, budget range..."
                    onChange={(e) => setMsgLen(e.target.value.length)}
                    className="border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-red-500 transition-colors resize-none"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  />
                </div>

                {status === 'error' && (
                  <p className="text-red-500 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting' || msgLen > 500}
                  className="w-full bg-red-700 hover:bg-red-800 disabled:opacity-60 text-white font-bold py-4 rounded-md text-base transition-colors cursor-pointer whitespace-nowrap mt-1"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {status === 'submitting' ? 'Sending...' : 'Get Your Free Quote →'}
                </button>

                <p className="text-center text-gray-400 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>
                  We respect your privacy. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
